import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import Carousel from 'components/Carousel';
import Container from 'components/Container';
import Row from 'components/Row';
import Stack from 'components/Stack';
import Text from 'components/Text';
import Link from 'components/Link';

const Section = styled.section`
  padding: 4.25rem 0;
  position: relative;
  z-index: 1;
`;

const StyledContainer = styled(Container)`
  max-width: calc(100% - 2rem);
  margin-block-end: 2.625rem;
`;

const StyledCarouselSlides = styled(Carousel.Slides)`
  /* Use negative block margin to prevent clipping of box-shadows */
  margin-bottom: -1.5rem;
  margin-top: -1.5rem;
  padding: 1.5rem 0;
`;

const StyledCard = styled(Card)`
  background-image: linear-gradient(
    rgb(29 41 57 / 60%) 35%,
    rgb(16 24 40 / 60%)
  );
  border: 0.125rem solid #344054;
  border-radius: 0.5rem;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px,
    rgba(0, 0, 0, 0.1) 0px 2px 4px -2px;
  inline-size: 23rem;
  max-inline-size: 75vw;
  padding: 1.5rem;
  position: relative;
  line-height: 1.625;
  text-wrap: balance;

  h3 {
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    font-weight: bold;
    line-height: 1.625;
    block-size: calc(1em * 1.625 * 2); /* fallback for lh */
    block-size: 2lh;
    overflow: hidden;
  }

  a {
    text-decoration: none;
  }

  p {
    -webkit-box-orient: vertical;
    -webkit-font-smoothing: antialiased;
    -webkit-line-clamp: 3;
    display: -webkit-box;
    line-height: 1.85;
    max-block-size: calc(1em * 1.85 * 3); /* fallback for lh */
    max-block-size: 3lh;
    overflow: hidden;
  }
`;

export default function DoraMetricsFeatureSection() {
  const data = useStaticQuery(
    graphql`
      query {
        allSanityPost(
          filter: {
            slug: {
              current: {
                in: ["meetings-vs-reviews", "ai-management-anti-patterns"]
              }
            }
          }
        ) {
          edges {
            node {
              title
              description
              slug {
                current
              }
              subcategory {
                title
              }
              image: featureImage {
                image {
                  asset {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                }
                alt
              }
            }
          }
        }
      }
    `
  );

  const posts = data.allSanityPost.edges.map(({ node }) => node);

  return (
    <Section>
      <Carousel
        alignSlides="stretch"
        gap="1rem"
        justifySlides="start"
        offset="max(1rem, calc((100vw - 64rem) / 2))"
      >
        <StyledContainer as={Stack} gap="2.625rem" size="md">
          <Text as="h2" variant="heading.section">
            Learn more about Reviews
          </Text>
          <Row
            align="start"
            colGap="6rem"
            rowGap="1.5rem"
            layout="auto"
            justify="end"
          >
            <Text variant="smallP">
              Sleuth turns dashboards into Reviews. Why would we do that? Learn
              more about Reviews vs. dashboards, and of course, how AI fits into
              all this.
            </Text>
            <Row breakpoint={null} gap="0.5rem">
              <Carousel.Control type="previous" variant="tertiary" />
              <Carousel.Control type="next" variant="tertiary" />
            </Row>
          </Row>
        </StyledContainer>
        <StyledCarouselSlides>
          {posts.map((item, index) => (
            <Carousel.Slide key={index}>
              <StyledCard {...item} />
            </Carousel.Slide>
          ))}
        </StyledCarouselSlides>
      </Carousel>
    </Section>
  );
}

function Card({ className, description, image, slug, title }) {
  return (
    <Stack as="article" gap="1rem" className={className}>
      {image && (
        <GatsbyImage
          image={getImage(image.image?.asset || image)}
          alt={image?.alt || title}
        />
      )}
      <Text as="h3" variant="smallP">
        <Link to={`/post/${slug.current}`} variant="fill">
          {title}
        </Link>
      </Text>
      <Text variant="smallestP">{description}</Text>
    </Stack>
  );
}
